import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  LoadingOverlay,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { IconEdit, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import { INT_MAX, setActionLog } from "../utils/util";
import { useDynamicScroll } from "../utils/useDynamicScroll";
import {
  labels,
  primaryActionProps,
  searchActionProps,
  secondaryActionProps,
} from "../constants/uiActions";
import { useModal } from "../contexts/useModal";
import { TerminalModelGenerate, TerminalModelUpdate } from "../components/Modal";
import { useCanWrite } from "../utils/useCanWrite";

interface TerminalModelRow {
  terminalModelId: number;
  internalModelName: string;
  externalModelName: string;
  serialKeyInjectType: number;
  vanType: number;
  terminalColor?: string;
  dikYn?: string;
  serialCableCount?: number;
  useYn: string;
  createdDate?: string;
  updatedDate?: string;
}

type CodeOption = { value: string; label: string };

const formatDate = (value?: string) => {
  if (!value) return "-";
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD HH:mm:ss") : value;
};

const TerminalModels: React.FC<{ menuTitle: string }> = ({ menuTitle }) => {
  const canWrite = useCanWrite();
  const [rows, setRows] = useState<TerminalModelRow[]>([]);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [selectedRow, setSelectedRow] = useState<TerminalModelRow | null>(null);

  const [injectTypeOptions, setInjectTypeOptions] = useState<CodeOption[]>([]);
  const [vanTypeOptions, setVanTypeOptions] = useState<CodeOption[]>([]);

  const { showModal } = useModal();

  // 단말기 관리 페이지와 동일: 창이 작아지면 body 스크롤 허용
  useDynamicScroll({ minHeight: 500 });

  const injectLabelMap = useMemo(
    () => Object.fromEntries(injectTypeOptions.map((o) => [o.value, o.label])),
    [injectTypeOptions],
  );
  const vanLabelMap = useMemo(
    () => Object.fromEntries(vanTypeOptions.map((o) => [o.value, o.label])),
    [vanTypeOptions],
  );

  const loadCodeOptions = useCallback(async () => {
    try {
      const [injectRes, vanRes] = await Promise.all([
        axiosInstance.get("/api/v1/common/serial-key-inject-type"),
        axiosInstance.get("/api/v1/common/van"),
      ]);
      if (
        injectRes.data?.code === "200" &&
        Array.isArray(injectRes.data.data)
      ) {
        const opts = injectRes.data.data.map(
          (item: { code: string; name: string }) => ({
            value: item.code,
            label: `${item.code}: ${item.name}`,
          }),
        );
        setInjectTypeOptions(opts);
      }
      if (vanRes.data?.code === "200" && Array.isArray(vanRes.data.data)) {
        const opts = vanRes.data.data.map(
          (item: { code: string; name: string }) => ({
            value: item.code,
            label: item.name,
          }),
        );
        setVanTypeOptions(opts);
      }
    } catch {
      toast.error("공통 코드 검색에 실패하였습니다.");
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        currentPageNo: 1,
        recordsPerPage: INT_MAX,
      };
      const q = keyword.trim();
      if (q) params.keyword = q;

      const res = await axiosInstance.get("/api/v1/terminal/model/list", {
        params,
      });
      if (res.data?.code === "200") {
        setRows(res.data.data?.list ?? []);
      } else {
        setRows([]);
        toast.error(res.data?.msg || labels.searchFailed);
      }
    } catch {
      toast.error(labels.searchFailed);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [keyword]);

  useEffect(() => {
    loadCodeOptions();
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTerminalModelGenerate = async () => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 모델 등록 버튼 클릭",
    });
    const flag = await showModal({
      component: TerminalModelGenerate,
      props: {},
    });
    if (flag) {
      toast.success("단말기 모델이 등록되었습니다.");
      setSelectedRow(null);
      fetchList();
    }
  };

  const handleTerminalModelUpdate = async () => {
    if (!selectedRow) {
      toast.error("수정할 모델을 목록에서 선택하세요.");
      return;
    }
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 모델 수정 버튼 클릭",
      content: selectedRow,
    });
    const flag = await showModal({
      component: TerminalModelUpdate,
      props: { row: selectedRow },
    });
    if (flag) {
      toast.success("사용 여부가 수정되었습니다.");
      setSelectedRow(null);
      fetchList();
    }
  };

  const handleSearch = async () => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 모델 검색 버튼 클릭",
    });
    fetchList();
  };

  const handleDelete = async (terminalModelId: number) => {
    if (!window.confirm("선택한 모델을 삭제하시겠습니까?")) return;

    try {
      const res = await axiosInstance.delete("/api/v1/terminal/model", {
        params: { terminalModelId },
      });
      if (res.data?.code === "200") {
        toast.success("단말기 모델이 삭제되었습니다.");
        if (selectedRow?.terminalModelId === terminalModelId) {
          setSelectedRow(null);
        }
        fetchList();
      } else {
        toast.error(res.data?.msg || "삭제에 실패하였습니다.");
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { msg?: string } } })?.response?.data
          ?.msg || "삭제에 실패하였습니다.";
      toast.error(msg);
    }
  };

  return (
    <div
      style={{
        height: "100%",
        background: "#fafbfc",
        borderRadius: "12px",
        padding: "0.75rem 1.5rem",
        display: "flex",
        flexDirection: "column",
        minHeight: "500px",
      }}
    >
      <LoadingOverlay
        visible={loading}
        zIndex={1000}
        overlayProps={{ radius: "sm", bg: "rgba(0,0,0,0.3)" }}
        loaderProps={{ color: "pink", type: "bars" }}
      />

      <Flex align="center" justify="space-between" pb="1rem">
        <Box fz="1.75rem" fw={700}>
          {menuTitle}
        </Box>
        <Flex gap="xs">
          <Button
            leftSection={<IconPlus size={20} stroke={1.5} />}
            onClick={handleTerminalModelGenerate}
            disabled={loading || !canWrite}
            {...primaryActionProps}
          >
            {labels.register}
          </Button>
          <Button
            leftSection={<IconEdit size={20} stroke={1.5} />}
            onClick={handleTerminalModelUpdate}
            disabled={loading || selectedRow == null || !canWrite}
            {...secondaryActionProps}
          >
            {labels.edit}
          </Button>
        </Flex>
      </Flex>

      <Text size="sm" c="dimmed" pb="0.5rem">
        등록·수정은 모달에서 진행합니다. 목록 행을 선택한 뒤 수정하면 사용
        여부(use_yn), DIK주입(dik_yn), 시리얼케이블(serial_cable_count) 등을 변경할 수 있습니다.
      </Text>

      <Flex align="flex-end" gap="xs" pb="1rem">
        <Text size="sm" fw={600} style={{ marginBottom: 8, marginRight: 8 }}>
          모델 목록 ({rows.length})
        </Text>
        <TextInput
          label="모델명"
          placeholder="내부/외부 모델명 검색"
          value={keyword}
          onChange={(e) => setKeyword(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <Button
          leftSection={<IconSearch size={20} stroke={1.5} />}
          onClick={handleSearch}
          disabled={loading}
          {...searchActionProps}
        >
          {labels.search}
        </Button>
      </Flex>

      <div
        style={{
          flex: 1,
          minHeight: "300px",
          overflow: "auto",
          scrollbarGutter: "stable",
          boxSizing: "border-box",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          borderRadius: 8,
          background: "#fff",
          position: "relative",
        }}
      >
        <Table>
          <Table.Thead>
            <Table.Tr bg="#f5f6fa" ta="left" pos="sticky" top={0}>
              <Table.Th p={12} pos="sticky" top={0}>
                ID
              </Table.Th>
              <Table.Th p={12} pos="sticky" top={0}>
                내부모델
              </Table.Th>
              <Table.Th p={12} pos="sticky" top={0}>
                외부모델
              </Table.Th>
              <Table.Th p={12} pos="sticky" top={0}>
                시리얼 키 주입 타입
              </Table.Th>
              <Table.Th p={12} pos="sticky" top={0}>
                VAN
              </Table.Th>
              <Table.Th p={12} pos="sticky" top={0}>
                색상
              </Table.Th>
              <Table.Th p={12} pos="sticky" top={0}>
                DIK주입
              </Table.Th>
              <Table.Th p={12} pos="sticky" top={0}>
                시리얼케이블
              </Table.Th>
              <Table.Th p={12} pos="sticky" top={0}>
                사용
              </Table.Th>
              <Table.Th p={12} pos="sticky" top={0}>
                수정일시
              </Table.Th>
              <Table.Th p={12} pos="sticky" top={0}>
                삭제
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length === 0 ? (
              <Table.Tr style={{ height: 30 }}>
                <Table.Td colSpan={11} p="4px 12px">
                  <Text size="sm" c="dimmed" ta="center">
                    등록된 모델이 없습니다.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows.map((row, idx) => {
                const isSelected =
                  selectedRow?.terminalModelId === row.terminalModelId;
                return (
                  <Table.Tr
                    key={row.terminalModelId}
                    onClick={() =>
                      setSelectedRow((prev) =>
                        prev?.terminalModelId === row.terminalModelId
                          ? null
                          : row,
                      )
                    }
                    bg={
                      isSelected
                        ? "var(--mantine-color-blue-light)"
                        : hoveredRow === idx
                          ? "#f0f4fa"
                          : undefined
                    }
                    fw={isSelected ? 700 : undefined}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHoveredRow(idx)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <Table.Td p="4px 12px">{row.terminalModelId}</Table.Td>
                    <Table.Td p="4px 12px">{row.internalModelName}</Table.Td>
                    <Table.Td p="4px 12px">{row.externalModelName}</Table.Td>
                    <Table.Td p="4px 12px">
                      {injectLabelMap[String(row.serialKeyInjectType)] ??
                        row.serialKeyInjectType}
                    </Table.Td>
                    <Table.Td p="4px 12px">
                      {vanLabelMap[String(row.vanType)] ?? row.vanType}
                    </Table.Td>
                    <Table.Td p="4px 12px">{row.terminalColor || "-"}</Table.Td>
                    <Table.Td p="4px 12px">{row.dikYn || "N"}</Table.Td>
                    <Table.Td p="4px 12px">{row.serialCableCount ?? 1}</Table.Td>
                    <Table.Td p="4px 12px">{row.useYn}</Table.Td>
                    <Table.Td p="4px 12px">
                      {formatDate(row.updatedDate)}
                    </Table.Td>
                    <Table.Td p="4px 12px">
                      <Button
                        size="xs"
                        radius="sm"
                        color="red"
                        leftSection={<IconTrash size={14} stroke={1.5} />}
                        disabled={!canWrite}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(row.terminalModelId);
                        }}
                      >
                        {labels.delete}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </div>
    </div>
  );
};

export default TerminalModels;
